// Stripe-only pass-through fee model.
//
// Two distinct fees exist; only the Stripe card fee may ever be passed to
// the client.
//   (a) Stripe card fee: 2.9% + $0.30 — surcharged to the client when the
//       per-company pass_through_fees_enabled toggle is ON.
//   (b) Prosperia platform fee: per-company prosperia_service_fee_rate —
//       ALWAYS deducted from the merchant payout via
//       application_fee_amount. Never appears on the client invoice and
//       never bundled into the client surcharge.
//
// payoutBase = servicesSubtotalAfterDiscount + taxAmount
//   pass-through ON:  clientTotal = (payoutBase + STRIPE_FEE_FIXED) / (1 - STRIPE_FEE_RATE)
//   pass-through OFF: clientTotal = payoutBase
//   ALWAYS:           applicationFeeCents = round(payoutBase * platformRate * 100)

export const STRIPE_FEE_RATE = 0.029
export const STRIPE_FEE_FIXED = 0.30

// Kill switches (#435). Both OFF until the surcharge rules (card-network caps,
// debit prohibition, state law) and platform pricing are settled.
//   SURCHARGING_ENABLED  — false: computeClientTotal never adds the card fee,
//                          whatever the company toggle or row snapshot says.
//   PLATFORM_FEE_ENABLED — false: computeApplicationFeeCents is always 0, so no
//                          application_fee_amount reaches Stripe.
// Every client/server money path prices through these two functions, so
// flipping a flag here (and in the Deno mirror) is the whole switch.
export const SURCHARGING_ENABLED = false
export const PLATFORM_FEE_ENABLED = false

// Single client-facing label for the Stripe card surcharge. Used on every
// invoice/estimate/widget/Stripe receipt so the customer sees the same wording.
// Never name the Prosperia platform fee here — it doesn't appear on client docs.
export const CLIENT_SURCHARGE_LABEL = 'Card processing fee'

// US jurisdictions where card-surcharging the customer is prohibited or so
// restricted we don't offer it from the UI. The merchant — not the platform —
// carries the penalty if they surcharge here, so we force the toggle OFF
// whenever business_address_state matches. Keep the rationale per entry so
// future-you can re-evaluate without re-researching.
//   CT, MA, ME — outright state-law bans on credit card surcharges.
//   CA         — Italian Colors left CA technically permissible but with
//                strict disclosure rules; we keep it off rather than ship
//                merchants into compliance trouble.
//   PR         — Puerto Rico bans surcharging by statute.
// ponytail: hard-coded list, move to a config table if state rules diverge
// per-merchant (e.g., a CT merchant licensed to surcharge under a network
// program). For now the static list matches the legal reality.
export const SURCHARGE_PROHIBITED_STATES = ['CT', 'MA', 'ME', 'CA', 'PR']

export const isSurchargeAllowedInState = (state) => {
  if (!state) return true
  return !SURCHARGE_PROHIBITED_STATES.includes(String(state).trim().toUpperCase())
}

const round2 = (n) => Math.round(n * 100) / 100

// Pure pass-through formula, ignoring the kill switch. Exported for the
// oracle test; callers use computeClientTotal.
export const computePassThroughTotal = ({ payoutBase, passThrough }) => {
  const base = Number(payoutBase) || 0
  if (base <= 0) {
    return { clientTotal: 0, clientSurcharge: 0 }
  }
  if (!passThrough) {
    return { clientTotal: round2(base), clientSurcharge: 0 }
  }
  const clientTotal = (base + STRIPE_FEE_FIXED) / (1 - STRIPE_FEE_RATE)
  return {
    clientTotal: round2(clientTotal),
    clientSurcharge: round2(clientTotal - base),
  }
}

export const computeClientTotal = ({ payoutBase, passThrough }) =>
  computePassThroughTotal({ payoutBase, passThrough: SURCHARGING_ENABLED && !!passThrough })

// Pure platform-fee formula, ignoring the kill switch. Exported for the
// oracle test; callers use computeApplicationFeeCents.
export const computePlatformFeeCents = ({ payoutBase, platformRate }) => {
  const base = Number(payoutBase) || 0
  const rate = Number(platformRate) || 0
  if (base <= 0 || rate <= 0) return 0
  return Math.round(base * rate * 100)
}

export const computeApplicationFeeCents = ({ payoutBase, platformRate }) =>
  PLATFORM_FEE_ENABLED ? computePlatformFeeCents({ payoutBase, platformRate }) : 0

// Rate-agnostic payoutBase recovery for an existing invoice/estimate row.
// Returns { payoutBase, passThrough } or { inconsistent: true, baseFromParts,
// baseFromTotal }. NEVER divides by an assumed rate — the legacy bundled
// population spanned two denominators, so any rate-dependent inverse
// mis-recovers on the other tier.
//
//   (a) baseFromParts = (services_subtotal − discount) + tax_amount
//   (b) baseFromTotal = total_amount − processing_fees_amount  (always)
//   When both derivable, cross-check; disagreement > $0.01 = inconsistent.
//   Otherwise prefer (a) when it exists, fall back to (b).
//
// Discount: services_subtotal is stored BEFORE discount (per the form path
// in InvoiceFormModal.jsx:854 and buildEstimatePayload.js:133), and
// tax_amount is computed on the AFTER-discount base. So baseFromParts must
// subtract the discount in dollars to match the create-path ordering
// exactly (services − discount + tax). Invoices don't carry a discount
// column (audit §1, also confirmed in _createInvoice), so discount fields
// resolve to 0 and the discounted-vs-undiscounted formula collapses.
//
// passThrough basis is the ROW's stored state (surcharge > $0.02), not the
// live company toggle. Legacy null-snapshot rows (services 0) fall to (b)
// with surcharge 0, never triggering a divide.
export const recoverPayoutBase = (row) => {
  const total = Number(row?.total_amount) || 0
  const surcharge = Number(row?.processing_fees_amount) || 0
  const services = Number(row?.services_subtotal) || 0
  const taxAmt = Number(row?.tax_amount) || 0
  const discountAmount = Number(row?.discount_amount) || 0
  const discountType = row?.discount_type || 'fixed'
  const discountDollars = discountType === 'percent'
    ? services * (discountAmount / 100)
    : discountAmount
  const afterDiscount = Math.max(0, services - discountDollars)
  const passThrough = surcharge > 0.02
  const baseFromParts = services > 0 ? round2(afterDiscount + taxAmt) : null
  const baseFromTotal = round2(Math.max(0, total - surcharge))
  if (baseFromParts !== null && Math.abs(baseFromParts - baseFromTotal) > 0.01) {
    return { inconsistent: true, baseFromParts, baseFromTotal, passThrough }
  }
  return { payoutBase: baseFromParts ?? baseFromTotal, passThrough }
}
